'use client';

/**
 * useNotifications Hook
 *
 * Main hook for notification operations including:
 * - Fetching notifications
 * - Marking as read/unread
 * - Archiving notifications
 * - Managing unread counts
 */

import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import logger from '@/lib/logger';
import {
  setNotifications,
  appendNotifications,
  setUnreadCounts,
  setLoading,
  setLoadingMore,
  setError,
  markAsRead,
  markMultipleAsRead,
  markAllAsRead,
  restoreUnread,
  archiveNotification,
  selectNotifications,
  selectUnreadCount,
  selectUnreadByCategory,
  selectIsLoading,
  selectIsLoadingMore,
  selectHasMore,
  selectActiveFilter,
  selectError,
} from '@/store/slices/notificationsSlice';
import type {
  Notification,
  NotificationCategory,
  NotificationListResponse,
  UnreadCountResponse,
} from '@/store/slices/notificationsTypes';

// Notifications are served by ui-frontend, not AI engine
const API_BASE = '/api';

function isLocalNotificationId(notificationId: string): boolean {
  return notificationId.startsWith('local-');
}

interface UseNotificationsOptions {
  /** Auto-fetch on mount */
  autoFetch?: boolean;
  /** Limit per fetch */
  limit?: number;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { autoFetch = true, limit = 20 } = options;
  const dispatch = useAppDispatch();

  // Selectors
  const notifications = useAppSelector(selectNotifications);
  const unreadCount = useAppSelector(selectUnreadCount);
  const unreadByCategory = useAppSelector(selectUnreadByCategory);
  const isLoading = useAppSelector(selectIsLoading);
  const isLoadingMore = useAppSelector(selectIsLoadingMore);
  const hasMore = useAppSelector(selectHasMore);
  const activeFilter = useAppSelector(selectActiveFilter);
  const error = useAppSelector(selectError);

  /**
   * Fetch notifications from API
   *
   * Supports smart filtering with:
   * - sourcePage: Filter to specific page
   * - includeSystem: Include system notifications (source_page IS NULL)
   * - minPriority: Include high/urgent notifications regardless of page
   */
  const fetchNotifications = useCallback(
    async (params?: {
      category?: NotificationCategory | 'all';
      offset?: number;
      append?: boolean;
      /** Filter by page path (e.g., '/calls', '/reports') */
      sourcePage?: string;
      /** Include system notifications (source_page IS NULL) */
      includeSystem?: boolean;
      /** Include high/urgent priority notifications regardless of page */
      minPriority?: 'high' | 'urgent';
    }) => {
      const {
        category,
        offset = 0,
        append = false,
        sourcePage,
        includeSystem = true, // Default to including system notifications
        minPriority,
      } = params || {};

      if (append) {
        dispatch(setLoadingMore(true));
      } else {
        dispatch(setLoading(true));
      }

      try {
        const queryParams = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        });

        if (category && category !== 'all') {
          queryParams.set('category', category);
        }

        // Smart page filtering
        if (sourcePage) {
          queryParams.set('source_page', sourcePage);
          if (includeSystem) {
            queryParams.set('include_system', 'true');
          }
          if (minPriority) {
            queryParams.set('min_priority', minPriority);
          }
        }

        const response = await fetch(
          `${API_BASE}/notifications?${queryParams}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          }
        );

        if (!response.ok) {
          // 401 is expected when user session expired or not logged in -- silently return empty
          if (response.status === 401) {
            const emptyData: NotificationListResponse = {
              notifications: [],
              total: 0,
              hasMore: false,
            };
            if (!append) {
              dispatch(setNotifications(emptyData));
            }
            return emptyData;
          }
          // Try to get error details from response body
          let errorDetails = response.statusText || `HTTP ${response.status}`;
          try {
            const errorBody = await response.json();
            if (errorBody.error) {
              errorDetails = errorBody.error;
            }
          } catch {
            // Ignore JSON parse errors for error body
          }
          throw new Error(`Failed to fetch notifications: ${errorDetails}`);
        }

        const data = await response.json();

        // Transform snake_case to camelCase (API returns has_more, we want hasMore)
        const transformedData: NotificationListResponse = {
          notifications: (data.notifications || []).map(transformNotification),
          total: data.total ?? 0,
          hasMore: data.hasMore ?? data.has_more ?? false,
        };

        if (append) {
          dispatch(appendNotifications(transformedData));
        } else {
          dispatch(setNotifications(transformedData));
        }

        return transformedData;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch notifications';
        dispatch(setError(message));
        throw err;
      }
    },
    [dispatch, limit]
  );

  /**
   * Fetch unread counts
   */
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/notifications/unread-count`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        // 401 is expected when user session expired or not logged in -- don't warn
        if (response.status === 401) {
          return;
        }
        throw new Error(`Failed to fetch unread counts: HTTP ${response.status}`);
      }

      const data: UnreadCountResponse = await response.json();

      dispatch(
        setUnreadCounts({
          total: data.total,
          byCategory: data.byCategory || data.by_category || {},
        })
      );

      return data;
    } catch (err) {
      logger.warn('useNotifications', 'Failed to fetch unread counts', { error: err });
      // Don't throw - unread count is not critical
    }
  }, [dispatch]);

  /**
   * Mark a notification as read
   */
  const handleMarkAsRead = useCallback(
    async (notificationId: string) => {
      // Optimistic update
      dispatch(markAsRead(notificationId));

      if (isLocalNotificationId(notificationId)) {
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE}/notifications/mark-read`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ notification_ids: [notificationId] }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to mark notification as read');
        }
      } catch (err) {
        logger.warn('useNotifications', 'Failed to mark as read', { error: err });
        // Refetch to sync state
        fetchUnreadCounts();
      }
    },
    [dispatch, fetchUnreadCounts]
  );

  /**
   * Mark multiple notifications as read
   */
  const handleMarkMultipleAsRead = useCallback(
    async (notificationIds: string[]) => {
      // Optimistic update
      dispatch(markMultipleAsRead(notificationIds));

      const persistentNotificationIds = notificationIds.filter((id) => !isLocalNotificationId(id));
      if (persistentNotificationIds.length === 0) {
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/notifications/mark-read`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ notification_ids: persistentNotificationIds }),
        });

        if (!response.ok) {
          throw new Error('Failed to mark notifications as read');
        }
      } catch (err) {
        logger.warn('useNotifications', 'Failed to mark multiple as read', { error: err });
        fetchUnreadCounts();
      }
    },
    [dispatch, fetchUnreadCounts]
  );

  /**
   * Mark all notifications as read
   */
  const handleMarkAllAsRead = useCallback(async () => {
    // Snapshot BEFORE the optimistic update so a refused request can be rolled
    // back even when the API is completely unreachable (extreme module 02
    // M4/M7, 2026-08-17: the old path only re-fetched the counts, which also
    // failed, so the badge stayed cleared and no error ever rendered).
    const snapshot = {
      ids: notifications.filter((n) => !n.isRead).map((n) => n.id),
      unreadCount,
      unreadByCategory: { ...unreadByCategory },
    };

    // Optimistic update
    dispatch(markAllAsRead());
    dispatch(setError(null));

    try {
      const response = await fetch(`${API_BASE}/notifications/mark-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ mark_all: true }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark all as read');
      }
    } catch (err) {
      logger.warn('useNotifications', 'Failed to mark all as read', { error: err });
      dispatch(restoreUnread(snapshot));
      dispatch(setError('Could not mark all as read — the notifications service did not respond.'));
      fetchUnreadCounts();
    }
  }, [dispatch, fetchUnreadCounts, notifications, unreadCount, unreadByCategory]);

  /**
   * Archive a notification
   */
  const handleArchive = useCallback(
    async (notificationId: string) => {
      // Optimistic update
      dispatch(archiveNotification(notificationId));

      if (isLocalNotificationId(notificationId)) {
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE}/notifications/archive`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ notification_ids: [notificationId] }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to archive notification');
        }
      } catch (err) {
        logger.warn('useNotifications', 'Failed to archive notification', { error: err });
        // Refetch to sync state
        fetchNotifications();
      }
    },
    [dispatch, fetchNotifications]
  );

  /**
   * Load more notifications (pagination)
   */
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    fetchNotifications({
      category: activeFilter === 'all' ? undefined : activeFilter,
      offset: notifications.length,
      append: true,
    });
  }, [fetchNotifications, isLoadingMore, hasMore, activeFilter, notifications.length]);

  /**
   * Refresh notifications (fire-and-forget, catches errors internally)
   */
  const refresh = useCallback(() => {
    fetchNotifications({
      category: activeFilter === 'all' ? undefined : activeFilter,
    }).catch(() => {
      // Error already dispatched to state by fetchNotifications
    });
    fetchUnreadCounts();
  }, [fetchNotifications, fetchUnreadCounts, activeFilter]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchNotifications().catch(() => {
        // Error already dispatched to state by fetchNotifications
      });
      fetchUnreadCounts();
    }
  }, [autoFetch, fetchNotifications, fetchUnreadCounts]);

  return {
    // Data
    notifications,
    unreadCount,
    unreadByCategory,

    // State
    isLoading,
    isLoadingMore,
    hasMore,
    error,

    // Actions
    fetchNotifications,
    fetchUnreadCounts,
    markAsRead: handleMarkAsRead,
    markMultipleAsRead: handleMarkMultipleAsRead,
    markAllAsRead: handleMarkAllAsRead,
    archive: handleArchive,
    loadMore,
    refresh,
  };
}

/**
 * Transform API response to match our types (snake_case to camelCase)
 */
function transformNotification(n: any): Notification {
  return {
    id: n.id,
    userId: n.user_id,
    orgUnitId: n.org_unit_id,
    category: n.category,
    priority: n.priority,
    behavior: n.behavior || 'actionable',
    title: n.title,
    body: n.body,
    icon: n.icon,
    actionUrl: n.action_url,
    actionLabel: n.action_label,
    metadata: n.metadata || {},
    sourcePage: n.source_page,
    isRead: n.is_read,
    readAt: n.read_at,
    isArchived: n.is_archived,
    deliveredInapp: n.delivered_inapp,
    deliveredPush: n.delivered_push,
    deliveredEmail: n.delivered_email,
    createdAt: n.created_at,
    expiresAt: n.expires_at,
  };
}

export default useNotifications;
